import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Text,
  Button,
  BlockStack,
  InlineStack,
  Spinner,
  Banner,
  Icon,
} from '@shopify/polaris';
import { ArrowLeftIcon, CheckCircleIcon } from '@shopify/polaris-icons';
import { useSite } from '../hooks/useSites';
import { useApplyCode, useCheckCode } from '../hooks/useAnalytics';
import { Layout } from '../components/Layout';

export function SetupScript() {
  const { siteId } = useParams<{ siteId: string }>();
  const navigate = useNavigate();

  const { data: site, isLoading: siteLoading, refetch } = useSite(siteId!);
  const applyCodeMutation = useApplyCode();
  const checkCodeMutation = useCheckCode();

  const [step1Success, setStep1Success] = useState(false);
  const [step3Result, setStep3Result] = useState<{
    checked: boolean;
    applied: boolean;
    message: string;
  } | null>(null);

  if (siteLoading) {
    return (
      <Layout>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
          <Spinner size="large" />
        </div>
      </Layout>
    );
  }

  const handleActivateCode = async () => {
    try {
      await applyCodeMutation.mutateAsync(siteId!);
      setStep1Success(true);
      refetch();
    } catch (error) {
      console.error('Failed to activate custom code:', error);
    }
  };

  const handleCheckCode = async () => {
    try {
      const result = await checkCodeMutation.mutateAsync(siteId!);
      setStep3Result({
        checked: true,
        applied: result.applied,
        message: result.message,
      });
      refetch();
    } catch (error) {
      console.error('Failed to check custom code:', error);
      setStep3Result({
        checked: true,
        applied: false,
        message: 'Failed to check tracking code',
      });
    }
  };

  const webflowDashboardUrl = site?.short_name
    ? `https://${site.short_name}.design.webflow.com`
    : 'https://webflow.com/dashboard';

  // Check if GA4 is configured
  const hasGA4Configured = !!site?.analytics?.measurement_id;

  return (
    <Layout>
      <BlockStack gap="600">
        {/* Header with back button */}
        <InlineStack gap="300" blockAlign="center">
          <Button
            icon={ArrowLeftIcon}
            variant="plain"
            onClick={() => navigate('/dashboard')}
            accessibilityLabel="Back to dashboard"
          />
          <Text as="h1" variant="headingLg" fontWeight="bold">
            Setup script for {site?.display_name}
          </Text>
        </InlineStack>

        {/* Show warning if GA4 not configured */}
        {!hasGA4Configured && (
          <Banner tone="warning">
            <BlockStack gap="200">
              <Text as="p" fontWeight="semibold">
                GA4 property not configured
              </Text>
              <Text as="p">
                You need to connect your Google account and select a GA4 property before activating the tracking code.
              </Text>
              <Button onClick={() => navigate(`/site/${siteId}/setup`)}>
                Configure GA4 Property
              </Button>
            </BlockStack>
          </Banner>
        )}

        {/* Step 1: Activate Custom Code */}
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <BlockStack gap="200">
              <Text as="h2" variant="headingMd" fontWeight="semibold">
                Step 1: Activate Custom Code script
              </Text>
              <Text as="p" tone="subdued">
                Add Custom code script of your site by clicking{' '}
                <Text as="span" fontWeight="semibold">Activate Custom Code</Text> button.
              </Text>
            </BlockStack>
            <div style={{ marginLeft: '40px' }}>
              {step1Success || site?.analytics?.custom_code_applied ? (
                <InlineStack gap="200" blockAlign="center">
                  <Icon source={CheckCircleIcon} tone="success" />
                  <Text as="span" tone="success" fontWeight="semibold">
                    Activated
                  </Text>
                </InlineStack>
              ) : (
                <Button
                  variant="primary"
                  loading={applyCodeMutation.isPending}
                  onClick={handleActivateCode}
                  disabled={!hasGA4Configured}
                >
                  Activate Custom Code
                </Button>
              )}
            </div>
          </div>
          {applyCodeMutation.isError && (
            <div style={{ marginTop: '16px' }}>
              <Banner tone="critical" onDismiss={() => applyCodeMutation.reset()}>
                Failed to activate custom code. Please try again.
              </Banner>
            </div>
          )}
        </Card>

        {/* Step 2: Publish site */}
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <BlockStack gap="200">
              <Text as="h2" variant="headingMd" fontWeight="semibold">
                Step 2: Publish site
              </Text>
              <Text as="p" tone="subdued">
                Publish your site to make the changes live. Click the button to go to your Webflow dashboard.
              </Text>
            </BlockStack>
            <div style={{ marginLeft: '40px' }}>
              <Button
                variant="primary"
                url={webflowDashboardUrl}
                external
              >
                Take me there
              </Button>
            </div>
          </div>
        </Card>

        {/* Step 3: Check site */}
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <BlockStack gap="200">
              <Text as="h2" variant="headingMd" fontWeight="semibold">
                Step 3: Check site
              </Text>
              <Text as="p" tone="subdued">
                After publishing your website, click the button to verify if the Custom Code has been added to your site.
              </Text>
            </BlockStack>
            <div style={{ marginLeft: '40px' }}>
              <Button
                variant="primary"
                loading={checkCodeMutation.isPending}
                onClick={handleCheckCode}
              >
                Check for Custom Code
              </Button>
            </div>
          </div>
          {step3Result && (
            <div style={{ marginTop: '16px' }}>
              {step3Result.applied ? (
                <Banner tone="success" onDismiss={() => setStep3Result(null)}>
                  <BlockStack gap="200">
                    <Text as="p" fontWeight="semibold">
                      Custom Code is active on your site!
                    </Text>
                    <Text as="p">
                      Your GA4 tracking code is working. You can now view your analytics reports.
                    </Text>
                    <Button onClick={() => navigate(`/site/${siteId}/standard-reports`)}>
                      View Reports
                    </Button>
                  </BlockStack>
                </Banner>
              ) : (
                <Banner tone="warning" onDismiss={() => setStep3Result(null)}>
                  <Text as="p">
                    {step3Result.message}. Please make sure you've published your Webflow site after activating the custom code.
                  </Text>
                </Banner>
              )}
            </div>
          )}
        </Card>
      </BlockStack>
    </Layout>
  );
}
